import { AccountInfo } from "@azure/msal-browser";
import { appSession, appStorage } from "../../../commons";
import {
  AppAvatar,
  AppBox,
  AppButton,
  AppDivider,
  AppLink,
  AppModal,
  AppPaper,
  AppText,
  AppTitle,
} from "../../../commons/components";
import { decodeJWT } from "../../../commons/utilities/blob-utils";
import { removeUser, setTheme, setUser } from "../../../redux/slices/session-slice";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../hooks/app";
import IconLogout from "../../../commons/components/icons/logout";
import IconPerson from "../../../commons/components/icons/person";
import { useEffect, useRef, useState } from "react";
import { User } from "../../../commons/models/user";

const ProfileToolbar = () => {
  const token = appStorage.getItem("authToken");
  const { user } = useAppSelector((state) => state.session);
  const { instance } = useMsal();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [profilePopup, setProfilePopup] = useState<boolean>(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const handleAuthClick = (e: Event) => {
    e.preventDefault();
    if (!user || !user.id) {
      navigate("/auth");
      return;
    }
    setProfilePopup(!profilePopup);
  };

  const logoutClick = () => {
    if (!token) {
      clearSessionAndNavigatePublic();
      return;
    }
    const tokenInfo = decodeJWT(token);
    if (!tokenInfo.iss || !tokenInfo.iss.includes("sts.windows.net")) {
      clearSessionAndNavigatePublic();
      return;
    }
    const loggedInAccount = appStorage.getItem("microsoftUser");
    if (!loggedInAccount) {
      clearSessionAndNavigatePublic();
      return;
    }
    const account = JSON.parse(loggedInAccount as string) as AccountInfo;
    let theme = appStorage.getItem("appTheme");
    appSession.clearSession();
    dispatch(setTheme(theme as "light" | "dark"));
    instance.logoutRedirect({
      account: account,
      logoutHint: account.homeAccountId,
    });
    navigate("/auth/microsoft/logout");
  };

  const clearSessionAndNavigatePublic = () => {
    let theme = appStorage.getItem("appTheme");
    appSession.clearSession();
    dispatch(setTheme(theme as "light" | "dark"));
    navigate("/");
  };

  useEffect(() => {
    // If no user in Redux, try restoring from localStorage
    if (!user?.id) {
      const storedUser = appSession.getUser() as User;
      if (storedUser) {
        try {
          dispatch(setUser(storedUser));
        } catch {
          console.warn("Invalid authUser data in localStorage.");
        }
      }
    }
  }, [user, dispatch]);

  useEffect(() => {
    const handleStorageChange = () => {
      const updatedUser = appSession.getUser() as User;
      if (updatedUser) {
        setUser(updatedUser as User);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        profilePopup &&
        popupRef.current &&
        !popupRef.current.contains(target) &&
        avatarRef.current &&
        !avatarRef.current.contains(target)
      ) {
        setProfilePopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profilePopup]);

  const handleSwitchView = () => {
    if (window.location.href.includes("/manage/")) {
      navigate("/");
    } else {
      navigate("/manage");
    }
  };

  const handleViewProfile = () => {
    if (window.location.href.includes("/manage/")) {
      navigate("/manage/me");
    } else {
      navigate("/me");
    }
  };

  return (
    <>
      <AppBox className="user-login">
        <AppBox className="user-avatar-container" ref={avatarRef}>
          <AppLink onClick={(e) => handleAuthClick(e)}>
            <AppAvatar
              size="md"
              username={user?.fullName||''}
              src={user?.avatar}
              icon={user?.fullName ? null : <IconPerson />}
              color={user ? "accent" : "dark"}
            />
          </AppLink>
          {profilePopup && (
            <AppBox className="user-avatar-popup" ref={popupRef}>
              <AppPaper padding="xs" shadow="sm" withBorder>
                <AppBox flexDirection="column" flexWrap="nowrap" gap="3xs">
                  <AppBox alignItems="center" gap="sm" flexWrap="nowrap">
                    <AppAvatar size="xl" username={user?.fullName ?? "user"} src={user?.avatar} color="accent" />
                    <AppBox flexDirection="column">
                      <AppTitle as="h6">{user?.fullName}</AppTitle>
                    </AppBox>
                  </AppBox>
                  <AppDivider my="2xs" />
                  <AppBox justifyContent="space-between" gap="2xs">
                    <AppButton
                      type="button"
                      variant="light"
                      label="View Profile"
                      size="xs"
                      onClick={() => {
                        handleViewProfile();
                        setProfilePopup(false);
                      }}
                    />
                    <AppButton
                      type="button"
                      variant="light"
                      label="Switch View"
                      size="xs"
                      color="warning"
                      onClick={() => handleSwitchView()}
                    />
                    <AppButton
                      type="button"
                      label="Logout"
                      color="danger"
                      size="xs"
                      leftSection={<IconLogout width={18} height={18} />}
                      onClick={() => {
                        setProfilePopup(false);
                        dispatch(removeUser());
                        logoutClick();
                      }}
                    />
                  </AppBox>
                </AppBox>
              </AppPaper>
            </AppBox>
          )}
        </AppBox>
      </AppBox>
    </>
  );
};

export default ProfileToolbar;
